import PersonIcon from "@mui/icons-material/Person";
import { Box, Chip, ListItem, Typography } from "@mui/material";
import { format } from "date-fns";

import { AmbulanzResponse } from "@/api";
import { ClickableRow } from "@/common/components/clickable-row/ClickableRow";
import { ClientLink } from "@/common/components/client-link/ClientLink";
import { EllipsisText } from "@/common/components/ellipsis-text/EllipsisText";
import { RowActionButton } from "@/common/components/row-action-button/RowActionButton";
import { useLanguageContext } from "@/common/contexts/language-context/LanguageContext";
import { locales } from "@/common/contexts/localization-date-context/LocalizationDateContextProvider.utils";
import { useTranslation } from "@/translations";

import { AmbulanzEditDialog } from "../ambulanz-edit/AmbulanzEditDialog";
import { formatAmbulanzLabel } from "./AmbulanzWidget.utils";
import { AmbulanzMenu } from "./components/menu/AmbulanzMenu";
import { useAmbulanz } from "./hooks/useAmbulanz";

interface Props {
  ambulanz: AmbulanzResponse;
}

export function AmbulanzWidget({ ambulanz }: Props) {
  const { language } = useLanguageContext();
  const { t } = useTranslation();
  const { isOpen, isClaimed, isOwn, isCompleted, claim, returnAmbulanz, handleComplete, isBusy, editing, setEditing } =
    useAmbulanz(ambulanz);

  const label = formatAmbulanzLabel(ambulanz, t);
  const clientName = (ambulanz.clientFullName ?? ambulanz.clientName ?? "").trim();

  const showOwnerActions = isOwn && isClaimed;

  return (
    <ListItem
      disablePadding
      sx={(theme) => ({
        borderBottom: "1px solid",
        borderColor: theme.palette.fsw["blue-10"],
        minHeight: "69px",
      })}
    >
      <ClickableRow onClick={() => setEditing(true)} sx={{ flex: 1, py: 1.5, gap: 1 }}>
        <Box flex={1} minWidth={0}>
          <RowActionButton onClick={() => setEditing(true)}>
            <EllipsisText value={label} variant="body1" color={isCompleted ? "text.secondary" : undefined} />
          </RowActionButton>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography variant="body2" color="text.secondary">
              {`${format(ambulanz.createdAt, "dd.MM.yyyy HH:mm", { locale: locales[language] })} ${t("common.clock")} ·`}
            </Typography>
            {ambulanz.clientId && ambulanz.clientFullName ? (
              <ClientLink clientId={ambulanz.clientId} name={ambulanz.clientFullName} variant="body2" />
            ) : (
              <Typography variant="body2" color="text.secondary">
                {clientName || t("ambulanzes.anonymous")}
              </Typography>
            )}
          </Box>
        </Box>
        {ambulanz.assignedCounselorCode && (
          <Chip
            size="small"
            variant="outlined"
            icon={<PersonIcon sx={{ fontSize: 14 }} />}
            label={ambulanz.assignedCounselorCode}
            sx={{ width: 72, flexShrink: 0 }}
          />
        )}
        <AmbulanzMenu
          showClaim={isOpen}
          showOwnerActions={showOwnerActions}
          onEdit={() => setEditing(true)}
          onClaim={claim}
          onReturn={returnAmbulanz}
          onComplete={handleComplete}
          isBusy={isBusy}
        />
      </ClickableRow>

      {editing && <AmbulanzEditDialog ambulanz={ambulanz} onClose={() => setEditing(false)} />}
    </ListItem>
  );
}
